import {
  AspectRatio,
  Text,
  Flex,
  type FlexProps,
  type AspectRatioProps,
  type TextProps,
  type SpaceProps,
  Link,
} from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import React, { type ReactNode } from "react";
import { ButtonGroup } from "ui/button";
import { Chip, type ChipProps } from "ui/chips";
import { type LinkProps } from "ui/link";

export type CardProps = {
  /**
   * The color theme of the card used for text colors.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The style variant of the card.
   *
   * @default "inline"
   */
  variant: "inline" | "shadow" | "fill";

  /**
   * The alignment of the card content.
   *
   * @default "left"
   */
  align?: "left" | "center";

  /**
   * The media to render in the card.
   *
   * Can be either an image or lottie animation
   */
  media: ReactNode;

  /**
   * The aspect ratio of the media.
   *
   * @default 1/1
   */
  mediaRatio?: AspectRatioProps["ratio"];

  /**
   * Optional chip that serves as lead-in text for the card.
   */
  chip?: {
    /**
     * The chip variant
     *
     * @default "transparent"
     */
    variant?: ChipProps["variant"];
    /**
     * The chip text.
     */
    text: string;
  };

  /**
   * The title of the card.
   */
  title: ReactNode;

  /**
   * The html element to use for the title.
   *
   * @default "h3"
   */
  titleAs?: TextProps["as"];

  /**
   * Optional description of the card.
   */
  description?: ReactNode;

  /**
   * The html element to use for the description.
   *
   * Please use a `div` when passing in MDX values or any other content that could
   * result in a [hydration error](https://nextjs.org/docs/messages/react-hydration-error).
   *
   * @default "p"
   */
  descriptionAs?: TextProps["as"];

  /**
   * Optional CTA buttons
   *
   * If using two, place the secondary button on the left, and the primary on the right.
   */
  actions?: ReactNode;

  /**
   * Optional href to use when the entire card acts as a link.
   *
   * Typically not used with actions.
   */
  href?: LinkProps["href"];

  /**
   * Target for opening a link, if `href` is included.
   */
  target?: LinkProps["target"];

  /**
   * Optional max width for the card.
   *
   * @default "{base: '100%', sm: remCalc(435)}"
   */
  maxW?: FlexProps["maxW"];

  /**
   * Optional max width for the text content only.
   */
  textMaxW?: FlexProps["maxW"];

  /**
   * Optional gap override between the card media and text.
   *
   * @default "big", see `theme/layer-styles.ts`
   */
  gap?: FlexProps["gap"];
} & SpaceProps &
  Pick<FlexProps, "_hover" | "_groupHover">;

export function Card({
  variant = "inline",
  theme = "light",
  align = "left",
  mediaRatio = 1 / 1,
  titleAs = "h3",
  descriptionAs = "p",
  maxW = { base: "100%", sm: "cardMaxW" },
  chip,
  title,
  description,
  actions,
  media,
  gap,
  textMaxW,
  href,
  target,
  ...spaceProps
}: CardProps) {
  const alignItems = align === "left" ? "start" : "center";
  const layerStyle = variant === "inline" ? "card.inline" : `card.${variant}.${theme}`;
  const linkProps = href ? { as: Link, variant: variant === "shadow" ? "shadowCard" : "unstyled", href, target } : null;

  return (
    <Flex
      layerStyle={layerStyle}
      align={alignItems}
      textAlign={align}
      maxW={maxW}
      gap={gap}
      {...linkProps}
      {...spaceProps}
    >
      <AspectRatio
        layerStyle="card.media"
        borderRadius={variant === "fill" ? "none" : undefined}
        bg={`brand.backgroundSecondary.${theme}`}
        ratio={mediaRatio}
      >
        {media}
      </AspectRatio>

      <Flex layerStyle={`card.textWrapper.${variant}`} align={alignItems} maxW={textMaxW} flex={1}>
        {chip && <Chip variant={chip.variant}>{chip.text}</Chip>}

        <Text as={titleAs} align={alignItems} color={`brand.text.${theme}`} textStyle="headline3">
          {title}
        </Text>

        {description && (
          <Text
            as={descriptionAs}
            color={`brand.textSecondary.${theme}`}
            textStyle="caption1"
            fontWeight="normal" // Need this to avoid inheriting semibold when used as a link card
          >
            {description}
          </Text>
        )}

        {actions && <ButtonGroup mt="auto">{actions}</ButtonGroup>}
      </Flex>
    </Flex>
  );
}
